import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAuthCode, oauthLogout } from "../../services/oauth.services";
import { clearSession, readSession } from "../../helpers/session";
import { updateUser } from "../../store/userSlice";
import {
  apiErrorMessage,
  buildErrorRedirectUrl,
  buildSuccessRedirectUrl,
  describeScopes,
  extractAuthCodeResult,
  getClientName,
  isPlausibleAuthCode,
  UNKNOWN_CLIENT_NAME,
  parseRedirectUriForResponse,
  validateAuthorizeParams,
} from "../../helpers/oauthHelpers";
import {
  OAUTH_ELIGIBLE_ROLES,
  OAUTH_ERRORS,
  OAUTH_INLINE_LOGIN_ROLES,
} from "../../constants/oauth";
import OAuthShell from "./Components/OAuthShell/OAuthShell";
import OAuthErrorCard from "./Components/OAuthErrorCard/OAuthErrorCard";
import ConsentCard from "./Components/ConsentCard/ConsentCard";
import OAuthLoginCard from "./Components/OAuthLoginCard/OAuthLoginCard";
import WrongAccountCard from "./Components/WrongAccountCard/WrongAccountCard";
import RedirectingCard from "./Components/RedirectingCard/RedirectingCard";
import { useTranslation } from "react-i18next";

/**
 * OAuth 2.0 + PKCE authorization endpoint (Claude MCP).
 *
 * All three scenarios -- consent, dedicated sign-in, wrong account type --
 * render in place at the same URL. This page NEVER calls navigate(), and never
 * renders a <Link> or <Navigate>, so the OAuth query string is structurally
 * impossible to lose. The only way out is window.location.replace() to an
 * allowlisted address.
 */
const Authorize = () => {
  const { t } = useTranslation(["auth", "common"]);
  const dispatch = useDispatch();
  const { search } = useLocation();
  const reduxUser = useSelector((state) => state.user);

  // The URL is the single source of truth. There is deliberately no
  // sessionStorage backup of the request: the page never navigates, so the URL
  // already survives a mid-flow refresh, and a stored copy would only create a
  // replay window in which a later bare visit to /authorize resurrects an
  // abandoned request and shows a phantom consent screen.
  const parsed = useMemo(
    () => validateAuthorizeParams(new URLSearchParams(search)),
    [search]
  );
  const { params, redirectUrl, fatal, oauthError } = parsed;

  const clientName = useMemo(() => {
    const name = getClientName(params?.client_id, redirectUrl);
    return name === UNKNOWN_CLIENT_NAME ? t("oauth.unknownClient") : name;
  }, [params, redirectUrl, t]);
  const redirectHost = redirectUrl ? redirectUrl.host : "";

  // A cross-origin access throw means we ARE framed, so default to framed.
  const framed = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }, []);

  const [session, setSession] = useState(readSession);
  // Lazy initialiser, not a mount effect, so there is no flash of the wrong
  // card before the correct one paints.
  const [step, setStep] = useState(() => {
    if (framed) return "framed";
    if (fatal) return "fatal";
    if (oauthError) return "protocol_error";
    const current = readSession();
    if (!current.token) return "login";
    if (!current.role) return "corrupt";
    return OAUTH_ELIGIBLE_ROLES.includes(current.role) ? "consent" : "wrong_role";
  });

  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [apiError, setApiError] = useState("");
  const [notice, setNotice] = useState("");

  // One-shot guard so a double click or a StrictMode double-invoke cannot mint
  // two authorization codes. Released only on failure.
  const inFlight = useRef(false);

  useEffect(() => {
    const previous = document.title;
    document.title = `Authorize ${clientName} - Hajex Bolt`;
    return () => {
      document.title = previous;
    };
  }, [clientName]);

  useEffect(() => {
    if (step === "corrupt") {
      // A Token with no Role is an unusable session -- start clean.
      clearSession(dispatch);
      setSession(readSession());
      setStep("login");
      return;
    }
    // userSlice hydrates from the localStorage "user" key at module load only.
    // If that key was missing or corrupt while "Token" survived, redux has no
    // token and the Bearer header would go out empty.
    const token = localStorage.getItem("Token");
    if (token && !reduxUser?.token) dispatch(updateUser({ token }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Protocol errors bounce back to the client, but with a visible countdown so
  // the user learns what happened instead of seeing a blank flash.
  useEffect(() => {
    if (step !== "protocol_error" || !redirectUrl || !oauthError) return undefined;
    const url = buildErrorRedirectUrl(
      redirectUrl,
      oauthError.error,
      oauthError.description,
      params?.state
    );
    const id = setTimeout(() => window.location.replace(url), 3000);
    return () => clearTimeout(id);
  }, [step, redirectUrl, oauthError, params]);

  // The single redirect trigger. `replace`, never href/assign, so Back cannot
  // re-fire a grant or replay a code-bearing URL.
  useEffect(() => {
    if (step === "redirecting" && target) window.location.replace(target);
  }, [step, target]);

  const leave = useCallback((url) => {
    setTarget(url);
    setStep("redirecting");
  }, []);

  // The echoed state ALWAYS comes from the validated request, never from an
  // API response, and is omitted entirely when the client sent none.
  const failToClient = useCallback(
    (error, description) => {
      if (!redirectUrl) {
        setStep("fatal");
        return;
      }
      leave(buildErrorRedirectUrl(redirectUrl, error, description, params?.state));
    },
    [redirectUrl, params, leave]
  );

  const handleApprove = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setApiError("");
    setSubmitting(true);

    const payload = {
      client_id: params.client_id,
      redirect_uri: params.redirect_uri,
      scope: params.scope,
      code_challenge: params.code_challenge,
      code_challenge_method: params.code_challenge_method,
    };
    if (params.state) payload.state = params.state;

    const { data, error, status } = await getAuthCode(payload);

    if (error) {
      if (status === 401 || status === 419) {
        // The session died mid-flow. That is not an OAuth error -- let the
        // user re-authenticate in place, with the request still intact.
        clearSession(dispatch);
        setSession(readSession());
        inFlight.current = false;
        setSubmitting(false);
        setNotice(
          "Your session expired. Please sign in again to finish connecting."
        );
        setStep("login");
        return;
      }
      if (status === 400 || status === 403 || status === 422) {
        // The authorization server rejected the request itself. Terminal.
        failToClient(
          status === 403 ? OAUTH_ERRORS.ACCESS_DENIED : OAUTH_ERRORS.INVALID_REQUEST,
          apiErrorMessage(error)
        );
        return;
      }
      // 5xx, timeout, network or CORS failure (status 0): stay put and let the
      // user retry. Burning the round trip on a transient blip is worse than a
      // second click.
      inFlight.current = false;
      setSubmitting(false);
      setApiError(
        status === 0
          ? t("oauth.unreachable")
          : apiErrorMessage(error)
      );
      return;
    }

    const out = extractAuthCodeResult(data);
    if (out.ok === false) {
      inFlight.current = false;
      setSubmitting(false);
      setApiError(out.message || t("oauth.authFailed"));
      return;
    }

    // A bare code always wins over any URL in the response, so the echoed
    // state is provably the one the client sent us. It is shape-checked first:
    // forwarding an HTML error page as ?code= would look like success to the
    // user and fail opaquely for the client.
    if (out.code && isPlausibleAuthCode(out.code)) {
      leave(buildSuccessRedirectUrl(redirectUrl, out.code.trim(), params.state));
      return;
    }
    // A server-supplied URL is followed only if it re-passes the allowlist AND
    // actually carries a code. A backend that merely echoed back the
    // redirect_uri it was sent would otherwise land the user on the client
    // with no code at all -- a silent failure that looks like success.
    if (out.redirectUrl) {
      const url = parseRedirectUriForResponse(out.redirectUrl);
      if (url && isPlausibleAuthCode(url.searchParams.get("code"))) {
        if (params.state && !url.searchParams.get("state")) {
          url.searchParams.set("state", params.state);
        }
        leave(url.toString());
        return;
      }
    }

    inFlight.current = false;
    setSubmitting(false);
    setApiError(t("oauth.unexpected"));
  }, [params, redirectUrl, dispatch, failToClient, leave, t]);

  const handleRetry = useCallback(() => {
    setApiError("");
    handleApprove();
  }, [handleApprove]);

  const handleDeny = useCallback(() => {
    failToClient(OAUTH_ERRORS.ACCESS_DENIED, "The user denied the request.");
  }, [failToClient]);

  // Every transition back into the consent card must start clean. Without this
  // a failed attempt's error banner (and its "Try again" button) survives the
  // sign-out / sign-in round trip and greets the NEXT account on a screen it
  // has not acted on yet.
  const resetTransient = useCallback(() => {
    inFlight.current = false;
    setSubmitting(false);
    setApiError("");
  }, []);

  const handleSwitchAccount = useCallback(async () => {
    resetTransient();
    setSwitching(true);
    await oauthLogout(); // bare instance; the wrapper swallows any failure
    clearSession(dispatch);
    setSession(readSession());
    setSwitching(false);
    setNotice(t("oauth.signedOutNotice"));
    setStep("login"); // same URL, same params -- no navigation
  }, [dispatch, resetTransient, t]);

  // Re-checks the RETURNED role, not the submitted one: the card posts
  // role "Shipper" but the backend may resolve ShipperUser, or the credentials
  // may belong to a Carrier. Signing in as a second non-shipper returns to the
  // wrong-account card rather than a dead end.
  //
  // Checked against OAUTH_INLINE_LOGIN_ROLES, NOT OAUTH_ELIGIBLE_ROLES: Admin
  // is eligible to CONSENT with a session it already had, but must never be
  // reachable by signing in here. Using the wider list would turn this page
  // into an admin login as soon as the backend resolved one, which is exactly
  // what the separate admin portal address exists to prevent.
  const handleLoggedIn = useCallback(
    (user) => {
      resetTransient();
      setNotice("");
      setSession(readSession());
      setStep(
        OAUTH_INLINE_LOGIN_ROLES.includes(user?.role) ? "consent" : "wrong_role"
      );
    },
    [resetTransient]
  );

  const renderCard = () => {
    if (step === "framed") {
      return (
        <OAuthErrorCard
          tone="warning"
          title={t("oauth.framedTitle")}
          message={t("oauth.framedMessage", { client: clientName })}
          primaryAction={{
            label: t("oauth.openNewTab"),
            onClick: () =>
              window.open(window.location.href, "_blank", "noopener"),
          }}
        />
      );
    }

    if (step === "fatal") {
      if (fatal?.kind === "no_params") {
        return (
          <OAuthErrorCard
            tone="info"
            title={t("oauth.noParamsTitle")}
            message={t("oauth.noParamsMessage")}
            primaryAction={{
              label: t("oauth.goToBolt"),
              onClick: () =>
                window.location.assign(session.token ? "/" : "/login/shipper"),
            }}
          />
        );
      }
      if (fatal?.kind === "bad_client") {
        return (
          <OAuthErrorCard
            title={t("oauth.badClientTitle")}
            message={t("oauth.badClientMessage")}
          />
        );
      }
      return (
        <OAuthErrorCard
          title={t("oauth.badRedirectTitle")}
          message={t("oauth.badRedirectMessage")}
          detail={fatal?.host ? t("oauth.requestedReturn", { host: fatal.host }) : undefined}
        />
      );
    }

    if (step === "protocol_error") {
      return (
        <OAuthErrorCard
          title={t("oauth.protocolTitle")}
          message={oauthError?.description}
          countdownSeconds={3}
          primaryAction={{
            label: t("oauth.returnNow", { client: clientName }),
            onClick: () =>
              window.location.replace(
                buildErrorRedirectUrl(
                  redirectUrl,
                  oauthError.error,
                  oauthError.description,
                  params?.state
                )
              ),
          }}
        />
      );
    }

    if (step === "redirecting") {
      return (
        <RedirectingCard
          label={t("oauth.returningTo", { client: clientName })}
          fallbackUrl={target}
        />
      );
    }

    if (step === "login") {
      return (
        <OAuthLoginCard
          clientName={clientName}
          notice={notice}
          onLoggedIn={handleLoggedIn}
          onCancel={handleDeny}
        />
      );
    }

    if (step === "wrong_role") {
      return (
        <WrongAccountCard
          clientName={clientName}
          user={session.user}
          role={session.role}
          switching={switching}
          onSwitchAccount={handleSwitchAccount}
          onCancel={handleDeny}
        />
      );
    }

    // step === "consent"
    if (switching) return <RedirectingCard label={t("oauth.signingOut")} />;
    return (
      <ConsentCard
        clientName={clientName}
        user={session.user}
        role={session.role}
        scopeItems={describeScopes(params?.scope)}
        redirectHost={redirectHost}
        submitting={submitting}
        apiError={apiError}
        onApprove={handleApprove}
        onDeny={handleDeny}
        onSwitchAccount={handleSwitchAccount}
        onRetry={apiError ? handleRetry : undefined}
      />
    );
  };

  return <OAuthShell clientName={clientName}>{renderCard()}</OAuthShell>;
};

export default Authorize;
